
import React, { useState } from 'react';

// Fallback to empty string if not defined
const API_ENDPOINT = process.env.VITE_CONTACT_API_URL || "";
const NOMINATED_EMAIL = process.env.VITE_CONTACT_EMAIL || "lookapp.au@gmail.com";

const Contact: React.FC = () => {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    interest: 'Custom Web App Design',
    details: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');

    try {
      if (!API_ENDPOINT) {
        // If no API endpoint is defined, simulate a successful network request for demo purposes
        console.warn("VITE_CONTACT_API_URL is not defined. Simulating a successful submission for demonstration.");
        await new Promise(resolve => setTimeout(resolve, 2000));
        setStatus('success');
        return;
      }

      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors', // Common for Google Apps Script or simple webhooks
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      // Artificial delay for smoother UX transition
      await new Promise(resolve => setTimeout(resolve, 1000));
      setStatus('success');
    } catch (error) {
      console.error("Submission error:", error);
      setStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-24 relative overflow-hidden bg-gradient-to-tr from-blue-50/80 via-white to-emerald-50/40 scroll-mt-24">
      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row gap-16">
          <div className="lg:w-1/2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-600/5 border border-blue-600/10 text-blue-600 text-xs font-black uppercase tracking-widest mb-6 shadow-sm">
              Connect With Us
            </div>
            <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-8 leading-tight">
              Ready to <span className="gradient-text">Level Up</span> Your Ops?
            </h2>
            <p className="text-slate-600 text-lg font-semibold mb-12 max-w-lg leading-relaxed">
              Bridge the gap between high-end design and complex enterprise logic. We are ready to help you build the future of your business.
            </p>
            
            <div className="space-y-6">
              <div className="group flex gap-6 p-6 rounded-3xl hover:bg-white/80 backdrop-blur-sm transition-all border border-transparent hover:border-blue-100 hover:shadow-xl hover:shadow-blue-500/5">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/20">
                  <i className="fas fa-envelope text-xl"></i>
                </div>
                <div>
                  <h4 className="text-slate-900 font-black text-lg">Email us</h4>
                  <p className="text-slate-500 font-bold">{NOMINATED_EMAIL}</p>
                </div>
              </div>
             
              <div className="group flex gap-6 p-6 rounded-3xl hover:bg-white/80 backdrop-blur-sm transition-all border border-transparent hover:border-emerald-100 hover:shadow-xl hover:shadow-emerald-500/5">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/20">
                  <i className="fas fa-map-marker-alt text-xl"></i>
                </div>
                <div>
                  <h4 className="text-slate-900 font-black text-lg">Location</h4>
                  <p className="text-slate-500 font-bold">Adelaide, South Australia</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:w-1/2">
            <div className="bg-white/90 backdrop-blur-xl p-8 md:p-12 rounded-[3rem] border border-slate-200 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.05)] relative min-h-[550px] flex flex-col justify-center">
              {status !== 'success' ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {status === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-100 text-red-600 text-sm font-bold rounded-2xl mb-4">
                      Something went wrong. Please try again or email us directly.
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Full Name</label>
                      <input 
                        type="text" name="name" required disabled={status === 'sending'}
                        value={formData.name} onChange={handleChange}
                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 text-slate-900 focus:outline-none focus:ring-4 focus:ring-blue-500/5 focus:border-blue-600/40 transition-all font-semibold" 
                        placeholder="John Doe" 
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
                      <input 
                        type="email" name="email" required disabled={status === 'sending'}
                        value={formData.email} onChange={handleChange}
                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 text-slate-900 focus:outline-none focus:ring-4 focus:ring-blue-500/5 focus:border-blue-600/40 transition-all font-semibold" 
                        placeholder="john@email.com" 
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Primary Interest</label>
                    <div className="relative">
                      <select 
                        name="interest" disabled={status === 'sending'}
                        value={formData.interest} onChange={handleChange}
                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 text-slate-900 focus:outline-none focus:ring-4 focus:ring-blue-500/5 focus:border-blue-600/40 transition-all appearance-none font-bold"
                      >
                        <option value="Custom Web App Design">Custom Web App Design</option>
                        <option value="New Website">New Website</option>
                        <option value="General Enquiry">General Enquiry</option>
                      </select>
                      <i className="fas fa-chevron-down absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"></i>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Project Details</label>
                    <textarea 
                      name="details" required disabled={status === 'sending'}
                      value={formData.details} onChange={handleChange}
                      className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 text-slate-900 h-32 focus:outline-none focus:ring-4 focus:ring-blue-500/5 focus:border-blue-600/40 transition-all resize-none font-semibold" 
                      placeholder="Tell us about your technical challenges..."
                    ></textarea>
                  </div>
                  <button 
                    type="submit" disabled={status === 'sending'}
                    className="group w-full py-5 bg-slate-900 text-white font-black rounded-2xl shadow-2xl hover:bg-slate-800 hover:shadow-slate-900/20 transition-all uppercase tracking-widest active:scale-[0.98] flex items-center justify-center gap-3"
                  >
                    {status === 'sending' ? (
                      <><i className="fas fa-circle-notch animate-spin"></i> Sending Transmission...</>
                    ) : (
                      <>Send Inquiry <i className="fas fa-paper-plane group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"></i></>
                    )}
                  </button>
                </form>
              ) : (
                <div className="text-center py-12 animate-in zoom-in-95 duration-500">
                  <div className="w-24 h-24 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center text-4xl mx-auto mb-8 shadow-sm">
                    <i className="fas fa-check"></i>
                  </div>
                  <h3 className="text-4xl font-black text-slate-900 mb-4">Transmission Sent</h3>
                  <p className="text-slate-500 font-bold mb-10 max-w-sm mx-auto leading-relaxed">We've received your request. Expect a response from us within 24 hours.</p>
                  <button 
                    onClick={() => setStatus('idle')}
                    className="px-10 py-4 bg-slate-100 text-slate-900 font-bold rounded-2xl hover:bg-slate-200 transition-all shadow-sm"
                  >
                    Send Another Project
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
